/**
 * PIN 시스템 마이그레이션 유틸리티
 * 개별 PIN에서 Master PIN 단일 시스템으로 전환
 */

import { collection, query, where, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../services/firebase';

const SECURE_NOTES_COLLECTION = 'secureNotes';
const SECURE_FILES_COLLECTION = 'secureFiles';

/**
 * 마이그레이션 상태 체크
 */
export async function checkMigrationStatus(userId) {
  try {
    const status = {
      needsMigration: false,
      notesWithIndividualPins: 0,
      filesWithIndividualPins: 0,
      totalItemsToMigrate: 0
    };

    // SecureNotes 체크
    const notesQuery = query(
      collection(db, SECURE_NOTES_COLLECTION),
      where('userId', '==', userId)
    );
    
    const notesSnapshot = await getDocs(notesQuery);
    notesSnapshot.forEach((docSnap) => {
      const data = docSnap.data();
      if (data.pinHash || data.pinSalt) {
        status.notesWithIndividualPins++;
        status.needsMigration = true;
      }
    });

    // SecureFiles 체크
    const filesQuery = query(
      collection(db, SECURE_FILES_COLLECTION),
      where('userId', '==', userId)
    );
    
    const filesSnapshot = await getDocs(filesQuery);
    filesSnapshot.forEach((docSnap) => {
      const data = docSnap.data();
      if (data.pinHash || data.pinSalt) {
        status.filesWithIndividualPins++;
        status.needsMigration = true;
      }
    });

    status.totalItemsToMigrate = status.notesWithIndividualPins + status.filesWithIndividualPins;
    
    return status;
  } catch (error) {
    console.error('Error checking migration status:', error);
    throw error;
  }
}

/**
 * 개별 PIN 제거 및 Master PIN 보호로 전환
 */
export async function migratePinSystem(userId, onProgress = null) {
  try {
    const results = {
      success: true,
      migratedNotes: 0,
      migratedFiles: 0,
      errors: [],
      totalMigrated: 0
    };

    // SecureNotes 마이그레이션
    const notesQuery = query(
      collection(db, SECURE_NOTES_COLLECTION),
      where('userId', '==', userId)
    );
    
    const notesSnapshot = await getDocs(notesQuery);
    const noteUpdates = [];
    
    notesSnapshot.forEach((docSnap) => {
      const data = docSnap.data();
      if (data.pinHash || data.pinSalt) {
        noteUpdates.push({
          ref: docSnap.ref,
          id: docSnap.id,
          type: 'note'
        });
      }
    });

    // 진행 상황 콜백
    let processed = 0;
    const totalItems = noteUpdates.length;

    for (const item of noteUpdates) {
      try {
        await updateDoc(item.ref, {
          isPinProtected: true,  // Master PIN으로 보호
          pinHash: null,         // 개별 PIN 해시 제거
          pinSalt: null,         // 개별 PIN 솔트 제거
          updatedAt: new Date()
        });
        
        results.migratedNotes++;
        processed++;
        
        if (onProgress) {
          onProgress({
            current: processed,
            total: totalItems,
            type: 'notes',
            message: `메모 ${processed}/${totalItems} 마이그레이션 중...`
          });
        }
      } catch (error) {
        console.error(`Error migrating note ${item.id}:`, error);
        results.errors.push({
          type: 'note',
          id: item.id,
          error: error.message
        });
      }
    }

    // SecureFiles 마이그레이션
    const filesQuery = query(
      collection(db, SECURE_FILES_COLLECTION),
      where('userId', '==', userId)
    );
    
    const filesSnapshot = await getDocs(filesQuery);
    const fileUpdates = [];
    
    filesSnapshot.forEach((docSnap) => {
      const data = docSnap.data();
      if (data.pinHash || data.pinSalt) {
        fileUpdates.push({
          ref: docSnap.ref,
          id: docSnap.id,
          type: 'file'
        });
      }
    });

    const totalFiles = fileUpdates.length;
    processed = 0;

    for (const item of fileUpdates) {
      try {
        await updateDoc(item.ref, {
          isPinProtected: true,  // Master PIN으로 보호
          pinHash: null,         // 개별 PIN 해시 제거
          pinSalt: null,         // 개별 PIN 솔트 제거
          updatedAt: new Date()
        });
        
        results.migratedFiles++;
        processed++;
        
        if (onProgress) {
          onProgress({
            current: processed,
            total: totalFiles,
            type: 'files',
            message: `파일 ${processed}/${totalFiles} 마이그레이션 중...`
          });
        }
      } catch (error) {
        console.error(`Error migrating file ${item.id}:`, error);
        results.errors.push({
          type: 'file',
          id: item.id,
          error: error.message
        });
      }
    }

    results.totalMigrated = results.migratedNotes + results.migratedFiles;
    results.success = results.errors.length === 0;

    return results;
  } catch (error) {
    console.error('Error during migration:', error);
    throw error;
  }
}

/**
 * 마이그레이션 롤백 (응급 상황용)
 * 주의: 이전 개별 PIN 데이터는 복구할 수 없음
 */
export async function rollbackMigration(userId) {
  try {
    const results = {
      rolledBackNotes: 0,
      rolledBackFiles: 0,
      errors: []
    };

    // PIN 보호된 메모들의 isPinProtected 플래그만 제거
    const notesQuery = query(
      collection(db, SECURE_NOTES_COLLECTION),
      where('userId', '==', userId),
      where('isPinProtected', '==', true)
    );
    
    const notesSnapshot = await getDocs(notesQuery);
    
    for (const docSnap of notesSnapshot.docs) {
      try {
        await updateDoc(docSnap.ref, {
          isPinProtected: false,
          updatedAt: new Date()
        });
        results.rolledBackNotes++;
      } catch (error) {
        results.errors.push({
          type: 'note',
          id: docSnap.id,
          error: error.message
        });
      }
    }

    // PIN 보호된 파일들의 isPinProtected 플래그만 제거
    const filesQuery = query(
      collection(db, SECURE_FILES_COLLECTION),
      where('userId', '==', userId),
      where('isPinProtected', '==', true)
    );
    
    const filesSnapshot = await getDocs(filesQuery);
    
    for (const docSnap of filesSnapshot.docs) {
      try {
        await updateDoc(docSnap.ref, {
          isPinProtected: false,
          updatedAt: new Date()
        });
        results.rolledBackFiles++;
      } catch (error) {
        results.errors.push({
          type: 'file',
          id: docSnap.id,
          error: error.message
        });
      }
    }

    return results;
  } catch (error) {
    console.error('Error during rollback:', error);
    throw error;
  }
}

/**
 * 마이그레이션 검증
 */
export async function validateMigration(userId) {
  try {
    const validation = {
      isValid: true,
      issues: [],
      stats: {
        totalNotes: 0,
        protectedNotes: 0,
        notesWithLegacyPins: 0,
        totalFiles: 0,
        protectedFiles: 0,
        filesWithLegacyPins: 0
      }
    };

    // SecureNotes 검증
    const notesQuery = query(
      collection(db, SECURE_NOTES_COLLECTION),
      where('userId', '==', userId)
    );
    
    const notesSnapshot = await getDocs(notesQuery);
    
    notesSnapshot.forEach((docSnap) => {
      const data = docSnap.data();
      validation.stats.totalNotes++;
      
      if (data.isPinProtected) {
        validation.stats.protectedNotes++;
      }
      
      // 레거시 PIN 필드가 남아있는지 체크
      if (data.pinHash || data.pinSalt) {
        validation.stats.notesWithLegacyPins++;
        validation.isValid = false;
        validation.issues.push({
          type: 'note',
          id: docSnap.id,
          issue: 'Legacy PIN fields still present'
        });
      }
    });

    // SecureFiles 검증
    const filesQuery = query(
      collection(db, SECURE_FILES_COLLECTION),
      where('userId', '==', userId)
    );
    
    const filesSnapshot = await getDocs(filesQuery);
    
    filesSnapshot.forEach((docSnap) => {
      const data = docSnap.data();
      validation.stats.totalFiles++;
      
      if (data.isPinProtected) {
        validation.stats.protectedFiles++;
      }
      
      // 레거시 PIN 필드가 남아있는지 체크
      if (data.pinHash || data.pinSalt) {
        validation.stats.filesWithLegacyPins++;
        validation.isValid = false;
        validation.issues.push({
          type: 'file',
          id: docSnap.id,
          issue: 'Legacy PIN fields still present'
        });
      }
    });

    return validation;
  } catch (error) {
    console.error('Error validating migration:', error);
    throw error;
  }
}

export default {
  checkMigrationStatus,
  migratePinSystem,
  rollbackMigration,
  validateMigration
};